import React, { useState } from 'react';
import { useLocation, useNavigate, useMatch } from 'react-router-dom';
import { useTournament } from '@shootout-comps/core';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import { LANG_OPTIONS } from '@shootout-comps/i18n';
import type { LangCode } from '@shootout-comps/i18n';
import { SnookerBallSpinner } from '../components/SnookerBall';

interface HeaderProps {
  // Platform-wide feature flag — hides the Leagues nav item entirely while the feature is
  // still experimental/in-development (see SuperuserSettingsPage's toggle).
  leaguesEnabled: boolean;
}

// Lets a superuser flip every admin-only check in the app (this header's own admin dropdown
// included) so they can see the site as a regular user would, without signing out. See
// useAuth().isSuperuser, which every admin-gated component reads instead of user.role directly.
function AdminPreviewToggle({ previewAsNonAdmin, onToggle, fullWidth }: { previewAsNonAdmin: boolean; onToggle: () => void; fullWidth?: boolean }) {
  return (
    <button
      onClick={onToggle}
      title={previewAsNonAdmin ? 'Viewing as a regular user — click to restore admin view' : 'Preview the app as a regular user would see it'}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.4rem',
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '999px', padding: '0.25rem 0.6rem 0.25rem 0.45rem',
        fontSize: '0.7rem', fontWeight: 600, color: 'var(--chalk-dim)',
        cursor: 'pointer', minHeight: '30px', width: fullWidth ? '100%' : undefined,
      }}
    >
      <span style={{
        width: '28px', height: '16px', borderRadius: '999px', flexShrink: 0,
        background: previewAsNonAdmin ? 'rgba(255,255,255,0.2)' : 'var(--gold)',
        position: 'relative', transition: 'background 0.2s',
      }}>
        <span style={{
          position: 'absolute', top: '2px', left: previewAsNonAdmin ? '2px' : '14px',
          width: '12px', height: '12px', borderRadius: '50%',
          background: previewAsNonAdmin ? 'var(--chalk-dim)' : 'var(--baize-dark)',
          transition: 'left 0.2s',
        }} />
      </span>
      {previewAsNonAdmin ? '👤 User view' : '👑 Admin view'}
    </button>
  );
}

export default function Header({ leaguesEnabled }: HeaderProps) {
  const { tournament } = useTournament();
  const { user, logout, isSuperuser, previewAsNonAdmin, setPreviewAsNonAdmin } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  // Whichever tournament is currently open for management (bracket/players), if any — a
  // user may own several tournaments, so this is never resolved implicitly, only from the URL.
  const tournamentMatch = useMatch('/tournament/:id/*');
  const openTournamentId = tournamentMatch?.params.id ?? null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  // The real account role, unaffected by the preview toggle — used only to decide whether the
  // toggle itself should be shown, so a previewing admin can always switch back.
  const isRealSuperuser = user?.role === 'superuser';

  const navItems: { path: string; label: string }[] = [
    ...(openTournamentId ? [
      { path: `/tournament/${openTournamentId}/bracket`, label: t.navBracket },
      // Once the draw has been made there's nothing left to "Add" — players are locked in.
      ...(tournament?.status === 'registration' ? [{ path: `/tournament/${openTournamentId}/players`, label: t.navPlayers }] : []),
    ] : []),
    { path: '/players', label: t.navPlayersAdmin },
    { path: '/tournaments', label: t.navHistory },
    ...(leaguesEnabled ? [{ path: '/leagues', label: t.navLeagues }] : []),
    { path: '/public', label: t.navPublic },
    { path: '/faq', label: t.navFaq },
    ...(!isSuperuser ? [{ path: '/contact', label: t.navContact }] : []),
  ];

  const adminItems: { path: string; label: string }[] = isSuperuser ? [
    { path: '/admin/users', label: t.navUsers },
    { path: '/admin/stats', label: '📊 Stats' },
    { path: '/admin/settings', label: '⚙ Settings' },
  ] : [];

  // Prefix match — e.g. both /tournament/:id/bracket and its .../match/:id sub-view highlight
  // "Bracket", not just exact top-level matches.
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isAdminPage = isSuperuser && location.pathname.startsWith('/admin/');

  const closeAll = () => { setMenuOpen(false); setLangOpen(false); setAdminOpen(false); };
  const handleNav = (path: string) => { navigate(path); closeAll(); };

  const statusLabel = tournament?.status === 'registration' ? t.statusRegistration
    : tournament?.status === 'active' ? t.statusActive
    : tournament?.status === 'complete' ? t.statusComplete : '';

  // Shared nav button style
  const navBtn = (active: boolean): React.CSSProperties => ({
    background: active ? 'var(--gold)' : 'transparent',
    color: active ? 'var(--baize-dark)' : 'var(--chalk-dim)',
    border: `1px solid ${active ? 'var(--gold)' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: '4px', padding: '0.3rem 0.7rem',
    fontSize: '0.75rem', fontWeight: 600,
    textTransform: 'uppercase' as const, letterSpacing: '0.06em',
    cursor: 'pointer', minHeight: '32px', transition: 'all 0.2s',
  });

  const mobileNavBtn = (active: boolean): React.CSSProperties => ({
    background: active ? 'rgba(201,168,76,0.15)' : 'transparent',
    color: active ? 'var(--gold)' : 'var(--chalk)',
    border: `1px solid ${active ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.08)'}`,
    borderRadius: '8px', padding: '0.85rem 1rem',
    fontSize: '1rem', fontWeight: active ? 600 : 400,
    cursor: 'pointer', textAlign: 'start' as const,
    marginBottom: '0.4rem', minHeight: '50px', width: '100%',
    display: 'block',
  });

  return (
    <>
      <header style={{
        background: 'linear-gradient(180deg, var(--rail) 0%, var(--rail-light) 100%)',
        borderBottom: '3px solid var(--gold)',
        height: 'var(--header-h)',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 4px 20px var(--shadow)',
        display: 'flex', alignItems: 'center',
        padding: '0 var(--page-pad)',
        gap: '0.75rem',
      }}>
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flex: 1, minWidth: 0 }}>
          <SnookerBallSpinner size={22} />
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 'clamp(1rem, 4vw, 1.4rem)', color: 'var(--gold)', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {tournament?.name ?? t.appName}
            </h1>
            {statusLabel && (
              <span style={{ fontSize: '0.6rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block' }}>
                {statusLabel}
              </span>
            )}
          </div>
        </div>

        {/* ── Desktop nav ── */}
        <nav style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }} className="desktop-nav">
          {navItems.map(({ path, label }) => (
            <button key={path} onClick={() => handleNav(path)} style={navBtn(isActive(path))}>
              {label}
            </button>
          ))}

          {/* Admin dropdown */}
          {isSuperuser && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setAdminOpen(!adminOpen); setLangOpen(false); setMenuOpen(false); }}
                style={{ ...navBtn(isAdminPage), display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                👑 Admin {adminOpen ? '▲' : '▼'}
              </button>
              {adminOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                  background: 'var(--rail)', border: '1px solid var(--gold)',
                  borderRadius: '8px', overflow: 'hidden', zIndex: 200,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: '150px',
                }}>
                  {adminItems.map(({ path, label }) => (
                    <button key={path} onClick={() => handleNav(path)} style={{
                      display: 'block', width: '100%', padding: '0.65rem 1rem',
                      background: isActive(path) ? 'rgba(201,168,76,0.15)' : 'transparent',
                      border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)',
                      color: isActive(path) ? 'var(--gold)' : 'var(--chalk)',
                      fontSize: '0.82rem', fontWeight: isActive(path) ? 600 : 400,
                      cursor: 'pointer', textAlign: 'left', minHeight: '40px',
                    }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Language picker — desktop only */}
        <div style={{ position: 'relative', flexShrink: 0 }} className="desktop-lang">
          <button
            onClick={() => { setLangOpen(!langOpen); setMenuOpen(false); setAdminOpen(false); }}
            style={{
              background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '4px', padding: '0.3rem 0.5rem', fontSize: '0.85rem',
              cursor: 'pointer', minHeight: '32px', display: 'flex', alignItems: 'center', gap: '0.3rem',
              color: 'var(--chalk)',
            }}
            aria-label={t.language}
          >
            {LANG_OPTIONS.find(l => l.code === lang)?.flag ?? '🌐'}
          </button>
          {langOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0,
              background: 'var(--rail)', border: '1px solid var(--gold)',
              borderRadius: '8px', overflow: 'hidden', zIndex: 200,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: '160px',
            }}>
              {LANG_OPTIONS.map(({ code, name, flag }) => (
                <button key={code} onClick={() => { setLang(code as LangCode); setLangOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    width: '100%', padding: '0.6rem 0.85rem',
                    background: code === lang ? 'rgba(201,168,76,0.15)' : 'transparent',
                    border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)',
                    color: code === lang ? 'var(--gold)' : 'var(--chalk)',
                    fontSize: '0.82rem', fontWeight: code === lang ? 600 : 400,
                    cursor: 'pointer', minHeight: '40px', textAlign: 'start',
                  }}>
                  <span>{flag}</span><span>{name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop: user + sign out */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }} className="desktop-user">
          {isRealSuperuser && <AdminPreviewToggle previewAsNonAdmin={previewAsNonAdmin} onToggle={() => setPreviewAsNonAdmin(!previewAsNonAdmin)} />}
          <button onClick={() => navigate('/profile')} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px', padding: '0.25rem 0.55rem', fontSize: '0.7rem', cursor: 'pointer', minHeight: '28px', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            {isSuperuser ? '👑' : '👤'} {user?.username}
          </button>
          <button onClick={logout} style={{
            background: 'transparent', color: 'var(--chalk-dim)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '4px', padding: '0.25rem 0.55rem',
            fontSize: '0.7rem', cursor: 'pointer', minHeight: '28px',
          }}>
            {t.signOut}
          </button>
        </div>

        {/* Mobile: profile button (always visible) + burger */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }} className="mobile-controls">
          <button
            onClick={() => { navigate('/profile'); closeAll(); }}
            style={{
              background: 'transparent', color: 'var(--chalk-dim)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '4px', padding: '0.3rem 0.5rem',
              fontSize: '0.75rem', cursor: 'pointer', minHeight: '36px',
              display: 'flex', alignItems: 'center', gap: '0.3rem',
            }}
          >
            {isSuperuser ? '👑' : '👤'} {user?.username}
          </button>
          <button
            onClick={() => { setMenuOpen(!menuOpen); setLangOpen(false); setAdminOpen(false); }}
            aria-label="Menu"
            style={{
              background: menuOpen ? 'rgba(201,168,76,0.15)' : 'transparent',
              border: `1px solid ${menuOpen ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.2)'}`,
              borderRadius: '4px', color: menuOpen ? 'var(--gold)' : 'var(--chalk)',
              fontSize: '1.2rem', padding: '0.3rem 0.5rem',
              cursor: 'pointer', minHeight: '36px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div style={{
          position: 'fixed', top: 'var(--header-h)', left: 0, right: 0, bottom: 0,
          background: 'rgba(15,45,28,0.97)', zIndex: 99,
          display: 'flex', flexDirection: 'column',
          padding: '1rem var(--page-pad)',
          overflowY: 'auto',
        }}>
          {isRealSuperuser && (
            <div style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <AdminPreviewToggle previewAsNonAdmin={previewAsNonAdmin} onToggle={() => setPreviewAsNonAdmin(!previewAsNonAdmin)} fullWidth />
            </div>
          )}

          {/* Nav items */}
          {navItems.map(({ path, label }) => (
            <button key={path} onClick={() => handleNav(path)} style={mobileNavBtn(isActive(path))}>
              {label}
            </button>
          ))}

          {/* Admin section */}
          {isSuperuser && adminItems.length > 0 && (
            <>
              <div style={{ fontSize: '0.63rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0.5rem 0 0.4rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                👑 Admin
              </div>
              {adminItems.map(({ path, label }) => (
                <button key={path} onClick={() => handleNav(path)} style={mobileNavBtn(isActive(path))}>
                  {label}
                </button>
              ))}
            </>
          )}

          {/* Language picker */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '0.5rem', paddingTop: '0.75rem' }}>
            <div style={{ fontSize: '0.63rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.4rem' }}>
              {t.language}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
              {LANG_OPTIONS.map(({ code, name, flag }) => (
                <button key={code} onClick={() => { setLang(code as LangCode); setMenuOpen(false); }}
                  style={{
                    background: code === lang ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${code === lang ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '6px', padding: '0.4rem 0.65rem',
                    color: code === lang ? 'var(--gold)' : 'var(--chalk)',
                    fontSize: '0.82rem', fontWeight: code === lang ? 600 : 400,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem',
                  }}>
                  {flag} {name}
                </button>
              ))}
            </div>
          </div>

          {/* Sign out */}
          <button onClick={() => { logout(); setMenuOpen(false); }} style={{
            background: 'rgba(192,57,43,0.12)', color: '#e74c3c',
            border: '1px solid rgba(192,57,43,0.25)', borderRadius: '8px',
            padding: '0.85rem 1rem', fontSize: '1rem', cursor: 'pointer',
            width: '100%', textAlign: 'start', minHeight: '50px',
          }}>
            {t.signOut}
          </button>
        </div>
      )}

      <style>{`
        .desktop-nav   { display: none !important; }
        .desktop-user  { display: none !important; }
        .desktop-lang  { display: none !important; }
        .mobile-controls { display: flex !important; }
        @media (min-width: 768px) {
          .desktop-nav      { display: flex !important; }
          .desktop-user     { display: flex !important; }
          .desktop-lang     { display: flex !important; }
          .mobile-controls  { display: none !important; }
          :root { --header-h: 64px; }
        }
      `}</style>
    </>
  );
}
